import { useCallback, useEffect, useRef, useState } from 'react';
import type { AppVersionPayload } from '@/lib/app-version-check';
import {
  fetchRemoteVersionInfo,
  getAppliedBuildId,
  getLocalBuildId,
  isHardRefreshCleanupPending,
  isNewerBuildAvailable,
  setAppliedBuildId,
  shouldAlignAppliedBuildId,
} from '@/lib/app-version-check';

export const APP_UPDATE_DISMISS_SESSION_KEY = 'jointo:app-update-dismissed-for';

/**
 * 等首屏渲染稳定后再提示，避免弹窗与加载态重叠。
 * 老标签页由 60s 轮询兜住，这里只影响「部署后新打开且有待清理记录」的用户，不必更短。
 */
const INITIAL_CHECK_DELAY_MS = 2_000;
/** 强制更新场景下旧客户端最长的暴露时间；version.json 仅百余字节，60s 轮询开销可忽略 */
const POLL_INTERVAL_MS = 60_000;
const MIN_CHECK_GAP_MS = 60_000;

export interface AppVersionCheckState {
  hasNewVersion: boolean;
  remoteBuildId: string | null;
  remoteVersion: string | undefined;
  /** 远端声明必须立即清理缓存并刷新，不可稍后 */
  forceHardRefresh: boolean;
  /** 代码已是最新，但强制更新要求的缓存清理还没走过（如用户手动刷新绕过了弹窗按钮） */
  cleanupPending: boolean;
  /** 本次发布的更新内容列表 */
  changelog: string[];
  dialogOpen: boolean;
  dismissDialogForLater: () => void;
  applyUpdateNow: () => Promise<void>;
}

export function useAppVersionCheck(
  applyReload: (targetBuildId?: string) => Promise<void>
): AppVersionCheckState {
  const localBuildId = getLocalBuildId();
  const [remoteInfo, setRemoteInfo] = useState<AppVersionPayload | null>(null);
  const [appliedBuildId, setAppliedBuildIdState] = useState<string | null>(() =>
    getAppliedBuildId()
  );
  const [dismissedForRemoteId, setDismissedForRemoteId] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem(APP_UPDATE_DISMISS_SESSION_KEY);
    } catch {
      return null;
    }
  });

  const lastInteractionCheckAtRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);

  const checkNow = useCallback(async () => {
    if (import.meta.env.DEV) return;

    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;

    // 维护模式（503）触发的被动刷新不属于版本更新，不写清理记录
    const remote = await fetchRemoteVersionInfo(ac.signal, () => applyReload());
    if (ac.signal.aborted) return;
    setRemoteInfo(remote);

    if (!remote) return;
    // 代码已最新且无待办清理时，把记录推进到当前构建，避免下次强制更新被误判为「清理未完成」
    const align = shouldAlignAppliedBuildId({
      remoteBuildId: remote.buildId,
      localBuildId,
      appliedBuildId: getAppliedBuildId(),
      requiresHardRefresh: remote.requiresHardRefresh,
    });
    if (!align) return;
    setAppliedBuildId(localBuildId);
    setAppliedBuildIdState(localBuildId);
  }, [applyReload, localBuildId]);

  const checkThrottledFromUserActivity = useCallback(async () => {
    if (import.meta.env.DEV) return;
    const now = Date.now();
    if (now - lastInteractionCheckAtRef.current < MIN_CHECK_GAP_MS) return;
    lastInteractionCheckAtRef.current = now;
    await checkNow();
  }, [checkNow]);

  useEffect(() => {
    if (import.meta.env.DEV) return;

    const t0 = window.setTimeout(() => {
      void checkNow();
    }, INITIAL_CHECK_DELAY_MS);

    // 后台标签页不轮询，回到前台由 visibilitychange 立即补一次
    const interval = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      void checkNow();
    }, POLL_INTERVAL_MS);

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void checkThrottledFromUserActivity();
      }
    };

    const onFocus = () => {
      void checkThrottledFromUserActivity();
    };

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);

    return () => {
      window.clearTimeout(t0);
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
      abortRef.current?.abort();
    };
  }, [checkNow, checkThrottledFromUserActivity]);

  const remoteBuildId = remoteInfo?.buildId ?? null;
  const remoteVersion = remoteInfo?.version;
  const forceHardRefresh = remoteInfo?.requiresHardRefresh === true;
  const changelog = remoteInfo?.changelog ?? [];

  const cleanupPending = isHardRefreshCleanupPending({
    remoteBuildId,
    localBuildId,
    appliedBuildId,
    requiresHardRefresh: forceHardRefresh,
  });
  const hasNewVersion = isNewerBuildAvailable(remoteBuildId, localBuildId) || cleanupPending;

  const modalSuppressed =
    hasNewVersion &&
    !forceHardRefresh &&
    dismissedForRemoteId !== null &&
    dismissedForRemoteId === remoteBuildId;
  const dialogOpen = hasNewVersion && !modalSuppressed;

  const dismissDialogForLater = useCallback(() => {
    if (forceHardRefresh) return;
    if (remoteBuildId) {
      try {
        sessionStorage.setItem(APP_UPDATE_DISMISS_SESSION_KEY, remoteBuildId);
      } catch {
        // ignore
      }
      setDismissedForRemoteId(remoteBuildId);
    }
  }, [remoteBuildId, forceHardRefresh, setDismissedForRemoteId]);

  const applyUpdateNow = useCallback(async () => {
    await applyReload(remoteBuildId ?? undefined);
  }, [applyReload, remoteBuildId]);

  return {
    hasNewVersion,
    remoteBuildId,
    remoteVersion,
    forceHardRefresh,
    cleanupPending,
    changelog,
    dialogOpen,
    dismissDialogForLater,
    applyUpdateNow,
  };
}
